import { Attribute, useConfigurator } from 'views/ProductPage/configurators/useConfigurator';
import {
  Validate,
  isValueConstraint,
  ValueConstraint
} from 'views/ProductPage/configurators/rules';
import connect from 'components/Shared/connect';
import { getConfiguratorVariables } from 'views/ProductPage/selector';
import { NumericInput } from 'views/ProductPage/configurators/numericInput';
import { onBlurField, onSelectValue } from '../actions';
import { IConstraint } from 'models/IConstraint';
import { ConstraintType } from 'models/ConstraintType';
import { rm } from 'utils';

type DoorsWidthAttribute = Attribute & {
  validate: Validate;
  findWidthVariant: (doorSize: number) => number;
  calcDoorWidth: (width: number, doors: number) => number;
  doors: string;
  autoAdjustValue: boolean;
  onBlur: () => void;
  onSelectValue: (key: string, value: string) => void;
};
export const DoorWidth = connect<
  DoorsWidthAttribute,
  Attribute,
  { inputs: { [key: string]: string } }
>(
  state => ({
    inputs: getConfiguratorVariables(state.currentPage)
  }),
  dispatch => ({
    onBlur: () => {
      dispatch(onBlurField());
    },
    onSelectValue: (key: string, value: string) => {
      dispatch(onSelectValue(key, value));
    }
  }),
  ({ inputs, ...state }, dispatch, props) => ({
    ...state,
    ...dispatch,
    ...props,
    doors: inputs.doors
  })
)(function _DoorWidth({
  findWidthVariant,
  validate,
  doors,
  calcDoorWidth,
  autoAdjustValue,
  onSelectValue,
  ...a
}: DoorsWidthAttribute) {
  const { onSelectChange, configurator } = useConfigurator();

  const selectDoorWidth = (doorSize: number) => {
    const variant = findWidthVariant(doorSize);
    variant && onSelectChange(a.id, variant.toString(), { emit: false });
  };

  const setCorrectDoorWidth = (result: ValueConstraint, input: string) => {
    const totalWidth = parseFloat(input);
    const _numberOfDoors = totalWidth / +result.value;
    const numberOfDoors =
      result.rule === ConstraintType.Max ? Math.ceil(_numberOfDoors) : Math.floor(_numberOfDoors);
    const doorSize = calcDoorWidth(totalWidth, numberOfDoors);
    const variant = findWidthVariant(doorSize);

    if (variant && autoAdjustValue) {
      onSelectChange('doors', numberOfDoors.toString());
      onSelectChange('widthMm', variant.toString(), { emit: false });
    }
  };

  const constraintAttributeMap: {
    [key: string]: (result: ValueConstraint, input: string) => void;
  } = {
    widthMm: result => {
      const MIN_WIDTH = 'widthMm';
      const minWidth = configurator.constraints.find(
        (constraint: IConstraint) =>
          constraint.rule === ConstraintType.Min && constraint.attribute === MIN_WIDTH
      );
      const value =
        isValueConstraint(minWidth) && minWidth.value > result.value
          ? minWidth.value
          : result.value;

      const variant = findWidthVariant(+value);
      onSelectValue(MIN_WIDTH, value.toString());

      if (variant) {
        onSelectChange(MIN_WIDTH, variant.toString(), { emit: false });
      }
    },
    doorWidth: setCorrectDoorWidth
  };
  const onBlur = (input: string) => {
    const doorSize = calcDoorWidth(parseFloat(input), parseInt(doors, 10));
    const validationArgs = [
      input ? ['widthMm', input] : [],
      doors ? ['doors', doors] : [],
      Number.isInteger(doorSize) ? ['doorWidth', doorSize.toString(), doors] : []
    ].filter(v => v.length > 1);

    const result = validationArgs.reduce(
      (result: IConstraint | undefined, [id, value, ...rest]) =>
        result !== undefined ? result : validate(id, value, ...rest),
      undefined
    );

    if (!result) {
      selectDoorWidth(doorSize);
    } else if (isValueConstraint(result)) {
      const fn = constraintAttributeMap[result.attribute];
      fn && fn(result, input);
    }

    setTimeout(a.onBlur, 0);
  };
  return (
    <NumericInput
      {...a}
      onBlur={onBlur}
      toolTip={rm('Product_Configurator_SlidingDoor_Width_ToolTip')}
    />
  );
});
